import { GapFillOptimizedFor } from "@faro-lotv/lotv";
import { create } from "zustand";

export type PostProcessingSettings = {
  /** Whether to enable post-processing. */
  isEnabled: boolean;

  /** If undefined, disabled. Otherwise use the given gap filling technique. */
  gapFillSettings?: GapFillOptimizedFor;

  /** Whether to use an ambient occlusion effect. */
  ambientOcclusionSettings: boolean;

  /** The strength of the eye dome effect. 0 means off. */
  eyeDomeStrength: number;

  /** Whether to show an outline for the eye dome effect. */
  eyeDomeOutline: boolean;

  /** Whether anti-aliasing is enabled. */
  antiAliasingSettings: boolean;
};

const defaultPostProcessingSettings: PostProcessingSettings = Object.freeze({
  isEnabled: true,
  gapFillSettings: GapFillOptimizedFor.Speed,
  ambientOcclusionSettings: true,
  eyeDomeStrength: 0.3,
  eyeDomeOutline: false,
  antiAliasingSettings: true,
});

export type PostProcessingSettingsStore = PostProcessingSettings & {
  setIsEnabled(value: boolean): void;
  setGapFilling(value: GapFillOptimizedFor | undefined): void;
  setAmbientOcclusion(value: boolean): void;
  setEyeDome(strength: number, outlines: boolean): void;
  setAntiAliasing(value: boolean): void;
};

/** Context containing settings for the post-processing of point clouds. */
export const usePostProcessingSettings = create<PostProcessingSettingsStore>(
  (set) => ({
    ...defaultPostProcessingSettings,
    setIsEnabled: (value) => set({ isEnabled: value }),
    setGapFilling: (value) => set({ gapFillSettings: value }),
    setAmbientOcclusion: (value) => set({ ambientOcclusionSettings: value }),
    setEyeDome: (strength, outline) =>
      set({ eyeDomeStrength: strength, eyeDomeOutline: outline }),
    setAntiAliasing: (value) => set({ antiAliasingSettings: value }),
  }),
);
